// Admin Feature Flags Component
// Manage feature flags using the backend API

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Flag, 
  CheckCircle, 
  XCircle, 
  Search,
  Info,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Users,
  Settings
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { toast } from '@/components/ui/use-toast';
import { useAdminFeatureFlags } from '@/hooks/useFeatureFlags.js';

const AdminFeatureFlags = () => {
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFlag, setEditingFlag] = useState(null);
  const [newFlag, setNewFlag] = useState({
    name: '',
    description: '',
    enabled: false,
    segments: ['free'],
    defaultValue: false
  });

  const {
    flags,
    segments,
    userOverrides,
    loading,
    error,
    createFlag,
    updateFlag,
    deleteFlag,
    setUserOverride,
    refresh,
  } = useAdminFeatureFlags();

  const handleCreateFlag = async () => {
    try {
      await createFlag(newFlag);
      setNewFlag({ name: '', description: '', enabled: false, segments: ['free'], defaultValue: false });
      setShowCreateForm(false);
      toast({
        title: "Feature flag created",
        description: `Flag "${newFlag.name}" has been created successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error creating flag",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateFlag = async (flagId, updates) => {
    try {
      await updateFlag(flagId, updates);
      toast({
        title: "Feature flag updated",
        description: "Flag has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error updating flag",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteFlag = async (flagId, flagName) => {
    if (!confirm(`Are you sure you want to delete the flag "${flagName}"?`)) {
      return;
    }

    try {
      await deleteFlag(flagId);
      toast({
        title: "Feature flag deleted",
        description: `Flag "${flagName}" has been deleted.`,
      });
    } catch (error) {
      toast({
        title: "Error deleting flag",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="loader-circle" size="xl" className="animate-spin text-brand-600" />
        <span className="ml-2 text-gray-600">Loading feature flags...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p className="font-medium">Failed to load feature flags</p>
        <p className="text-sm mt-2">{error.message}</p>
        <Button onClick={refresh} className="mt-4">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  // Filter flags based on search
  const filteredFlags = flags.filter(flag => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      flag.name.toLowerCase().includes(searchLower) ||
      flag.description.toLowerCase().includes(searchLower) ||
      flag.key.toLowerCase().includes(searchLower)
    );
  });

  const enabledCount = flags.filter(flag => flag.enabled).length;
  const totalCount = flags.length;

  const FeatureFlagItem = ({ flag }) => (
    <div className="flex items-start justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <Switch
            checked={flag.enabled}
            onCheckedChange={(enabled) => handleUpdateFlag(flag.id, { enabled })}
            className="mr-2"
          />
          <h4 className="font-semibold text-gray-900">{flag.name}</h4>
          <Badge variant={flag.enabled ? 'default' : 'secondary'}>
            {flag.enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
        <p className="text-sm text-gray-600 ml-8">{flag.description}</p>
        <div className="flex items-center gap-2 ml-8 mt-2">
          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{flag.key}</code>
          <span className="text-xs text-gray-500">•</span>
          <span className="text-xs text-gray-500">Default: {flag.defaultValue ? 'true' : 'false'}</span>
          <span className="text-xs text-gray-500">•</span>
          <span className="text-xs text-gray-500">Segments: {flag.segments.join(', ')}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEditingFlag(flag)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDeleteFlag(flag.id, flag.name)}
          className="text-red-600 hover:text-red-700"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          <p className="text-gray-600">Manage feature flags and user segments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={refresh} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Flag
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Features</p>
                <p className="text-3xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <Flag className="w-8 h-8 text-brand-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Enabled</p>
                <p className="text-3xl font-bold text-green-600">{enabledCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">User Overrides</p>
                <p className="text-3xl font-bold text-blue-600">{userOverrides.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search feature flags..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Feature Flags List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Feature Flags ({filteredFlags.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredFlags.map((flag) => (
              <FeatureFlagItem key={flag.id} flag={flag} />
            ))}
            {filteredFlags.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No feature flags found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Flag Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Feature Flag</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={newFlag.name}
                  onChange={(e) => setNewFlag({ ...newFlag, name: e.target.value })}
                  placeholder="Feature flag name"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newFlag.description}
                  onChange={(e) => setNewFlag({ ...newFlag, description: e.target.value })}
                  placeholder="Feature flag description"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newFlag.enabled}
                    onCheckedChange={(enabled) => setNewFlag({ ...newFlag, enabled })}
                  />
                  <Label>Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newFlag.defaultValue}
                    onCheckedChange={(defaultValue) => setNewFlag({ ...newFlag, defaultValue })}
                  />
                  <Label>Default Value</Label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleCreateFlag}>Create Flag</Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminFeatureFlags;
